import { isDefined, toError } from "@clipboard-health/util-ts";
import { setAuthHeaderProvider } from "@src/appV2/Auth/cognito/authHeaderProviderRef";
import { getTokenExpiryInSeconds, isTokenNearExpiry } from "@src/appV2/Auth/cognito/tokenUtils";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import constate from "constate";
import { useCallback, useEffect, useRef } from "react";

const BEARER_TOKEN_PREFIX = "Bearer ";
const MAX_COGNITO_ERROR_LOGS = 5;

/**
 * Hook that provides centralized authentication header values for API calls.
 * Manages the migration from Firebase to Cognito authentication based on feature flags.
 *
 * Returns values ready for the Authorization header:
 * - **Cognito** (feature flag enabled): `Bearer <token>` (OAuth 2.0 standard)
 * - **Firebase** (fallback): `<token>` (legacy format)
 *
 * Implements rate-limited logging to prevent log flooding.
 *
 * @returns An object with getAuthHeaderValue function that returns the formatted auth value
 */
function useAuthHeaderProvider() {
  const isCognitoInApiCallsEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ENABLE_COGNITO_IN_API_CALLS],
  });

  const isCognitoOnboardingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_ONBOARDING, {
    defaultValue: false,
  });
  const isCognitoFailClosed = useCbhFlag(CbhFeatureFlag.COGNITO_FAIL_CLOSED, {
    defaultValue: false,
  });

  // Track what we've already logged this session to prevent log flooding
  const loggedEvents = useRef({
    tokenUnavailable: false,
    flagsPartiallyEnabled: false,
    errorCount: 0,
  });

  const cognitoAuth = getCognitoSingleton();
  const firebaseAuth = getFirebaseSingleton();

  const getAuthHeaderValue = useCallback(
    async (forceRefresh?: boolean): Promise<string | undefined> => {
      const cognitoUser = await cognitoAuth.getCurrentUserInfo();
      const firebaseUser = firebaseAuth.currentUser;
      const isCognitoUserDefined = isDefined(cognitoUser);
      const isFirebaseUserDefined = isDefined(firebaseUser);

      if (!isCognitoUserDefined && !isFirebaseUserDefined) {
        return undefined;
      }

      const shouldUseCognito = getShouldUseCognito({
        isCognitoInApiCallsEnabled,
        isCognitoOnboardingEnabled,
        isCognitoUserDefined,
        isFirebaseUserDefined,
        hasCbhUserId: isDefined(cognitoUser?.cbhUserId),
      });

      if (shouldUseCognito) {
        try {
          return await generateCognitoToken(cognitoAuth, forceRefresh);
        } catch (maybeError: unknown) {
          if (loggedEvents.current.errorCount < MAX_COGNITO_ERROR_LOGS) {
            logError(APP_V2_APP_EVENTS.FAILED_TO_GET_COGNITO_TOKEN, {
              error: toError(maybeError),
              metadata: { cognitoFailClosed: isCognitoFailClosed },
            });
            loggedEvents.current.errorCount += 1;
          }

          if (isCognitoFailClosed || !firebaseAuth.currentUser) {
            throw new Error("Unable to get user token");
          }
        }
      }

      // Firebase fallback (legacy format without Bearer prefix)
      return await firebaseAuth.currentUser?.getIdToken(forceRefresh);
    },
    [
      isCognitoInApiCallsEnabled,
      isCognitoOnboardingEnabled,
      isCognitoFailClosed,
      cognitoAuth,
      firebaseAuth,
    ]
  );

  useEffect(() => {
    setAuthHeaderProvider(getAuthHeaderValue);
    return () => {
      setAuthHeaderProvider(undefined);
    };
  }, [getAuthHeaderValue]);

  return { getAuthHeaderValue };
}

interface ShouldUseCognitoParams {
  isCognitoInApiCallsEnabled: boolean;
  isCognitoOnboardingEnabled: boolean;
  isCognitoUserDefined: boolean;
  isFirebaseUserDefined: boolean;
  hasCbhUserId: boolean;
}

function getShouldUseCognito(params: ShouldUseCognitoParams): boolean {
  const {
    isCognitoInApiCallsEnabled,
    isCognitoOnboardingEnabled,
    isCognitoUserDefined,
    isFirebaseUserDefined,
    hasCbhUserId,
  } = params;

  // Use Cognito when API feature flag is enabled and Cognito user exists
  const isUserCognitoWithApiFeatureFlagEnabled = isCognitoInApiCallsEnabled && isCognitoUserDefined;
  // Fall back to Cognito when API flag is disabled but only Cognito user exists
  const isUserCognitoWithApiFeatureFlagDisabled =
    !isCognitoInApiCallsEnabled && isCognitoUserDefined && !isFirebaseUserDefined;
  // Use Cognito for onboarding users when feature flag is enabled
  const isUserCognitoOnboardingWithFeatureFlagEnabled =
    isCognitoOnboardingEnabled && isCognitoUserDefined && !hasCbhUserId;
  // Fall back to Cognito for onboarding users when flag is disabled but only Cognito exists
  const isUserCognitoOnboardingWithFeatureFlagDisabled =
    !isCognitoOnboardingEnabled && !isFirebaseUserDefined && isCognitoUserDefined && !hasCbhUserId;

  return (
    isUserCognitoWithApiFeatureFlagEnabled ||
    isUserCognitoWithApiFeatureFlagDisabled ||
    isUserCognitoOnboardingWithFeatureFlagEnabled ||
    isUserCognitoOnboardingWithFeatureFlagDisabled
  );
}

/**
 * Token expiry threshold in seconds.
 * Tokens will be proactively refreshed when they're within this
 * many seconds of expiring, to avoid edge cases where a token
 * expires between validation and API call completion.
 */
const TOKEN_EXPIRY_THRESHOLD_SECONDS = 60;

async function generateCognitoToken(
  cognitoAuth: ReturnType<typeof getCognitoSingleton>,
  forceRefresh?: boolean
): Promise<string | undefined> {
  // If force refresh is requested, skip expiry check
  if (forceRefresh) {
    const cognitoToken = await cognitoAuth.getIdToken(true);
    if (cognitoToken) {
      return `${BEARER_TOKEN_PREFIX}${cognitoToken}`;
    }
    return undefined;
  }

  // Get token without forcing refresh first
  const cognitoToken = await cognitoAuth.getIdToken(false);
  if (!cognitoToken) {
    return undefined;
  }

  // Check if token is expired or near expiry
  // This works around an Amplify v6 bug where fetchAuthSession() doesn't
  // auto-refresh tokens for CUSTOM_WITHOUT_SRP auth flow
  // See: https://github.com/aws-amplify/amplify-js/issues/14618
  if (isTokenNearExpiry(cognitoToken, TOKEN_EXPIRY_THRESHOLD_SECONDS)) {
    const expiryInSeconds = getTokenExpiryInSeconds(cognitoToken);
    logEvent(APP_V2_APP_EVENTS.AUTH_TOKEN_PROACTIVE_REFRESH, {
      tokenExpiryInSeconds: expiryInSeconds,
      thresholdSeconds: TOKEN_EXPIRY_THRESHOLD_SECONDS,
    });

    const refreshedToken = await cognitoAuth.getIdToken(true);
    if (refreshedToken) {
      return `${BEARER_TOKEN_PREFIX}${refreshedToken}`;
    }
    // If refresh fails but the token is still valid, keep using it
    if (expiryInSeconds > 0) {
      return `${BEARER_TOKEN_PREFIX}${cognitoToken}`;
    }
    return undefined;
  }

  return `${BEARER_TOKEN_PREFIX}${cognitoToken}`;
}

export const [AuthHeaderProvider, useAuthHeaderContext] = constate(useAuthHeaderProvider);
